const RELOAD_SESSION_KEY = "dwelldrop:reload-for-version";

/** True when we just hard-reloaded after the user accepted a new-version prompt. */
export function isPostVersionReloadNavigation(): boolean {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(RELOAD_SESSION_KEY) === "1";
}

function markPostVersionReload(): void {
  try {
    sessionStorage.setItem(RELOAD_SESSION_KEY, "1");
  } catch {
    // ignore quota / private mode
  }
}

function clearPostVersionReloadMark(): void {
  try {
    sessionStorage.removeItem(RELOAD_SESSION_KEY);
  } catch {
    // ignore
  }
}

function waitForServiceWorkerControl(timeoutMs: number): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return Promise.resolve();
  }
  if (navigator.serviceWorker.controller) return Promise.resolve();

  return new Promise((resolve) => {
    const timeout = window.setTimeout(resolve, timeoutMs);
    const onChange = (): void => {
      navigator.serviceWorker.removeEventListener("controllerchange", onChange);
      window.clearTimeout(timeout);
      resolve();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onChange);
  });
}

async function activateWaitingServiceWorker(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return;

  const waiting = registration.waiting;
  if (waiting) {
    waiting.postMessage({ type: "SKIP_WAITING" });
    await waitForServiceWorkerControl(4000);
    return;
  }

  try {
    await registration.update();
  } catch {
    // ignore
  }

  const installing = registration.installing;
  if (installing) {
    await new Promise<void>((resolve) => {
      const timeout = window.setTimeout(resolve, 4000);
      const onStateChange = (): void => {
        if (installing.state === "activated") {
          installing.removeEventListener("statechange", onStateChange);
          window.clearTimeout(timeout);
          resolve();
        }
      };
      installing.addEventListener("statechange", onStateChange);
    });
    await waitForServiceWorkerControl(2000);
  }
}

/**
 * Activates a waiting service worker (when possible), then hard-reloads so HTML
 * and precache align with the latest deploy.
 */
export async function reloadForNewVersion(): Promise<void> {
  markPostVersionReload();
  await activateWaitingServiceWorker();
  window.location.reload();
}

/** Call once after boot when {@link isPostVersionReloadNavigation} is true. */
export function clearPostVersionReloadNavigationMark(): void {
  clearPostVersionReloadMark();
}
